import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  X,
  Send,
  Trash2,
  AlertCircle,
  Video
} from 'lucide-react';
import { askRagAssistant } from '../../services/ragService';
import { getRoleDisplayName } from '../../utils/roleUtils';
import {
  loadAiAssistantChat,
  saveAiAssistantChat,
} from '../../utils/aiAssistantChatStorage';
import './AiAssistantWidget.css';

const SUGGESTED_QUESTIONS = [
  'What are the steps to run a follow-up query?',
  'How do I manage device inventory serials?',
  'How do I review monthly billing evidence?'
];

const HTTP_URL_RE = /(https?:\/\/[^\s<>"'）)]+)/g;

function toDrivePreviewUrl(url) {
  const id = String(url || '').match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  return id ? `https://drive.google.com/file/d/${id[1]}/preview` : null;
}

function formatInline(text, onOpenVideo) {
  const parts = String(text || '').split(/(\*\*[^*]+?\*\*|https?:\/\/[^\s<>"'）)]+)/g);
  return parts.filter(Boolean).map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    if (/^https?:\/\//i.test(part)) {
      const href = part.replace(/[),.;]+$/, '');
      const preview = toDrivePreviewUrl(href);
      if (preview && onOpenVideo) {
        return (
          <button
            key={idx}
            type="button"
            className="evitals-ai-inline-link evitals-ai-inline-video"
            onClick={() => onOpenVideo({ url: preview, label: 'Walkthrough' })}
          >
            Watch video
          </button>
        );
      }
      return (
        <a
          key={idx}
          className="evitals-ai-inline-link"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {href}
        </a>
      );
    }
    return <span key={idx}>{part.replace(/\*/g, '')}</span>;
  });
}

function parseWatchLine(line) {
  const trimmed = String(line || '').trim();
  const urlMatch = trimmed.match(HTTP_URL_RE);
  if (!urlMatch) return null;
  const url = urlMatch[0].replace(/[),.;]+$/, '');
  const looksLikeWatch = /watch|walkthrough|video|youtube|vimeo|loom|drive\.google/i.test(trimmed) || /^https?:\/\//i.test(trimmed);
  if (!looksLikeWatch) return null;
  const label = trimmed
    .replace(HTTP_URL_RE, '')
    .replace(/^[-•–]\s*/, '')
    .replace(/[:–—-]\s*$/, '')
    .trim() || 'Watch this walkthrough';
  return { url, label };
}

function isSectionHeading(line) {
  const t = String(line || '').trim();
  if (!t || /^\d+\.\s/.test(t) || /^[-•–]\s/.test(t)) return false;
  if (t.length > 72) return false;
  if (/^(from |then |after |demographics|care programs|programs|rpm\b|ccm\b)/i.test(t)) {
    return !/[.!?]$/.test(t) || t.length < 40;
  }
  return t.length <= 48 && !/[.!?]$/.test(t) && /^[A-Z]/.test(t);
}

function parseAssistantBlocks(text) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim()
      .replace(/---+/g, ' – ')
      .replace(/^[ \t]{0,3}#{1,6}[ \t]+/, '');

    if (!trimmed || /^[-*_]{3,}$/.test(trimmed)) {
      i += 1;
      continue;
    }

    const watch = parseWatchLine(trimmed);
    if (watch) {
      blocks.push({ type: 'video', ...watch });
      i += 1;
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    if (/^[-•–]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-•–]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-•–]\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (isSectionHeading(trimmed)) {
      blocks.push({ type: 'heading', text: trimmed.replace(/:$/, '') });
      i += 1;
      continue;
    }

    blocks.push({ type: 'p', text: trimmed });
    i += 1;
  }

  const grouped = [];
  for (let b = 0; b < blocks.length; b += 1) {
    const cur = blocks[b];
    const next = blocks[b + 1];
    if (cur.type === 'heading' && next && (next.type === 'ol' || next.type === 'ul')) {
      grouped.push({ type: 'card', title: cur.text, list: next });
      b += 1;
      continue;
    }
    grouped.push(cur);
  }
  return grouped;
}

/**
 * Floating AI Assistant Widget for eVitals
 *
 * Connected directly to Express backend RAG endpoint (POST /api/chat/rag).
 * Automatically scopes context with the logged-in user's role.
 * Chat history persists for the logged-in user until logout (or Clear).
 */
const AiAssistantWidget = () => {
  const { user } = useSelector((state) => state.auth);
  const userRole =
    getRoleDisplayName(user?.role_id) || user?.role_name || user?.role || 'User';
  const userId = user?.id ?? user?.user_id ?? null;
  const storageKey = userId != null ? String(userId) : null;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const skipNextSaveRef = useRef(false);

  // Restore chat for this user (survives close/reopen; App-level mount survives navigation)
  useEffect(() => {
    if (!storageKey) {
      setMessages([]);
      return;
    }
    skipNextSaveRef.current = true;
    setMessages(loadAiAssistantChat(storageKey));
  }, [storageKey]);

  // Persist while logged in — never overwrite storage with the empty pre-hydrate state
  useEffect(() => {
    if (!storageKey) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    saveAiAssistantChat(storageKey, messages);
  }, [messages, storageKey]);

  // Auto-scroll to bottom of message list
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [messages, isOpen]);

  // Adjust textarea height dynamically
  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 90)}px`;
    }
  };

  // Send message to backend RAG service
  const handleSendMessage = async (customText = null) => {
    const textToSend = typeof customText === 'string' ? customText : inputMessage;
    if (!textToSend || textToSend.trim() === '' || isLoading) return;

    const trimmed = textToSend.trim();
    setErrorBanner(null);

    const userMsg = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: trimmed,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    try {
      const data = await askRagAssistant(trimmed, userRole);

      const assistantMsg = {
        id: `assistant-${Date.now()}`,
        sender: 'assistant',
        text: data.answer || 'No answer generated.',
        sources: data.sources || [],
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      setErrorBanner(err.message || 'Failed to get answer from assistant.');

      const errorMsg = {
        id: `assistant-err-${Date.now()}`,
        sender: 'assistant',
        text: 'Sorry, I encountered an error while retrieving documentation for your request. Please try again.',
        sources: [],
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setErrorBanner(null);
  };

  const toggleWidget = () => {
    setIsOpen((prev) => !prev);
  };

  // Existing sidebar Help FAB opens this panel — no second floating trigger.
  useEffect(() => {
    const onToggle = () => setIsOpen((prev) => !prev);
    const onOpen = () => setIsOpen(true);
    window.addEventListener('evitals-ai-toggle', onToggle);
    window.addEventListener('evitals-ai-open', onOpen);
    return () => {
      window.removeEventListener('evitals-ai-toggle', onToggle);
      window.removeEventListener('evitals-ai-open', onOpen);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('evitals-ai-open', isOpen);
    return () => document.body.classList.remove('evitals-ai-open');
  }, [isOpen]);

  useEffect(() => {
    if (!activeVideo) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setActiveVideo(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeVideo]);

  // Format markdown into clean structured React elements
  const renderFormattedMessage = (text) => {
    if (!text) return null;

    const cleanText = text
      .replace(/\*Source:.*?\*/gi, '')
      .replace(/\[Source:.*?\]/gi, '')
      .replace(/^[ \t]*[-*_]{3,}[ \t]*$/gm, '')
      .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, '')
      .replace(/```/g, '')
      .trim();

    const blocks = parseAssistantBlocks(cleanText);

    const renderList = (list) => {
      const ListTag = list.type === 'ol' ? 'ol' : 'ul';
      return (
        <ListTag className={`evitals-ai-list evitals-ai-list-${list.type}`}>
          {list.items.map((item, idx) => (
            <li key={idx}>{formatInline(item, setActiveVideo)}</li>
          ))}
        </ListTag>
      );
    };

    return (
      <div className="evitals-ai-formatted-text">
        {blocks.map((block, index) => {
          if (block.type === 'card') {
            return (
              <div key={index} className="evitals-ai-path">
                <div className="evitals-ai-path-title">{formatInline(block.title, setActiveVideo)}</div>
                {renderList(block.list)}
              </div>
            );
          }
          if (block.type === 'heading') {
            return (
              <div key={index} className="evitals-ai-heading">
                {formatInline(block.text, setActiveVideo)}
              </div>
            );
          }
          if (block.type === 'video') {
            return (
              <button
                key={index}
                type="button"
                className="evitals-ai-video"
                onClick={() => {
                  const preview = toDrivePreviewUrl(block.url) || block.url;
                  setActiveVideo({ url: preview, label: block.label });
                }}
              >
                <Video size={14} aria-hidden="true" />
                <span>{block.label}</span>
              </button>
            );
          }
          if (block.type === 'ol' || block.type === 'ul') {
            return <div key={index}>{renderList(block)}</div>;
          }
          return (
            <p key={index} className="evitals-ai-para">
              {formatInline(block.text, setActiveVideo)}
            </p>
          );
        })}
      </div>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Chat drawer only — trigger is the existing sidebar Help FAB */}
      <div className="evitals-ai-window" role="dialog" aria-label="eVitals AI Assistant">
          {/* Header */}
          <div className="evitals-ai-header">
            <div className="evitals-ai-header-info">
              <h5 className="evitals-ai-header-title">eVitals AI Assistant</h5>
            </div>

            <div className="evitals-ai-header-actions">
              {messages.length > 0 && (
                <button
                  className="evitals-ai-header-btn"
                  onClick={clearChat}
                  title="Clear conversation"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                className="evitals-ai-header-btn"
                onClick={toggleWidget}
                title="Close assistant"
                aria-label="Close assistant"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages Body */}
          <div className="evitals-ai-messages">
            {errorBanner && (
              <div className="evitals-ai-error">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={14} />
                  <span>{errorBanner}</span>
                </div>
                <button
                  onClick={() => setErrorBanner(null)}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {messages.length === 0 && (
              <div className="evitals-ai-welcome">
                <h6>Welcome to eVitals AI Assistant!</h6>
                <p>Ask any operational question about the platform, clinical procedures, or user manual.</p>

                <div className="evitals-ai-suggested-list">
                  {SUGGESTED_QUESTIONS.map((q, idx) => (
                    <button
                      key={idx}
                      className="evitals-ai-suggested-btn"
                      onClick={() => handleSendMessage(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`evitals-ai-msg ${msg.sender === 'user' ? 'evitals-ai-msg-user' : 'evitals-ai-msg-assistant'
                  }`}
              >
                <div className="evitals-ai-bubble">
                  {msg.sender === 'assistant' ? renderFormattedMessage(msg.text) : msg.text}
                </div>
                <span className="evitals-ai-msg-time">{msg.timestamp}</span>
              </div>
            ))}

            {isLoading && (
              <div className="evitals-ai-msg evitals-ai-msg-assistant">
                <div className="evitals-ai-bubble evitals-ai-typing">
                  <span className="evitals-ai-dot" />
                  <span className="evitals-ai-dot" />
                  <span className="evitals-ai-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Footer / Input Area */}
          <div className="evitals-ai-footer">
            <div
              className="evitals-ai-input-wrapper"
              onMouseDown={(event) => {
                if (event.target.closest('.evitals-ai-send-btn')) return;
                if (event.target !== textareaRef.current) {
                  event.preventDefault();
                  textareaRef.current?.focus();
                }
              }}
            >
              <textarea
                ref={textareaRef}
                className="evitals-ai-textarea"
                placeholder="Ask about eVitals procedures..."
                rows={1}
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <button
                className="evitals-ai-send-btn"
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputMessage.trim()}
                title="Send message"
                aria-label="Send message"
              >
                <Send size={15} />
              </button>
            </div>
          </div>
        </div>

        {activeVideo && (
          <div
            className="evitals-ai-video-overlay"
            onClick={() => setActiveVideo(null)}
            role="presentation"
          >
            <div
              className="evitals-ai-video-modal"
              role="dialog"
              aria-modal="true"
              aria-label={activeVideo.label}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="evitals-ai-video-modal-header">
                <h6>{activeVideo.label}</h6>
                <button
                  type="button"
                  className="evitals-ai-header-btn"
                  onClick={() => setActiveVideo(null)}
                  aria-label="Close video"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="evitals-ai-video-frame">
                <iframe
                  src={activeVideo.url}
                  title={activeVideo.label}
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default AiAssistantWidget;
